import React, { useState, useEffect, useCallback } from "react";
import {
  Zap,
  Plus,
  Search,
  History,
  MoreVertical,
  Briefcase,
  User,
  Building,
  CheckSquare,
  Edit,
  Trash2,
  Copy,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import type { Automation, TriggerItem } from "./types";
import { TRIGGER_ITEM_LABELS, TRIGGER_EVENT_LABELS } from "./constants";
import {
  fetchAutomations,
  toggleAutomation,
  deleteAutomation,
  createAutomation,
} from "./automationsApi";
import { AutomationVisualBuilder } from "./AutomationVisualBuilder";
import { AutomationHistoryView } from "./AutomationHistoryView";
import { useCRM } from "../context/CRMContext";

export const AutomationsPage: React.FC = () => {
  const { currentCrm } = useCRM();
  const currentCrmId = currentCrm?.id;

  // View state: 'list' | 'builder'
  const [activeView, setActiveView] = useState<"list" | "builder">("list");
  const [activeTab, setActiveTab] = useState<string>("automacoes");
  const [selectedAutomation, setSelectedAutomation] =
    useState<Automation | null>(null);

  // Lista de automações
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [itemFilter, setItemFilter] = useState<string>("todos");
  const [statusFilter, setStatusFilter] = useState<string>("todos");

  // Carregar automações
  const loadAutomations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAutomations(currentCrmId);
      setAutomations(res.data || []);
    } catch (err: any) {
      setError(err?.message || "Erro ao carregar automações.");
    } finally {
      setLoading(false);
    }
  }, [currentCrmId]);

  useEffect(() => {
    loadAutomations();
  }, [loadAutomations]);

  // Alternar ativa/inativa
  const handleToggle = async (e: React.MouseEvent, auto: Automation) => {
    e.stopPropagation();
    const prev = auto.ativa;
    // Otimista
    setAutomations((list) =>
      list.map((a) => (a.id === auto.id ? { ...a, ativa: !prev } : a)),
    );

    try {
      await toggleAutomation(auto.id, currentCrmId);
    } catch {
      // Reverte em caso de erro
      setAutomations((list) =>
        list.map((a) => (a.id === auto.id ? { ...a, ativa: prev } : a)),
      );
    }
  };

  // Excluir automação
  const handleDelete = async (e: React.MouseEvent, autoId: number) => {
    e.stopPropagation();
    if (!window.confirm("Deseja realmente excluir esta automação?")) return;

    try {
      await deleteAutomation(autoId, currentCrmId);
      setAutomations((list) => list.filter((a) => a.id !== autoId));
    } catch (err: any) {
      alert(err?.message || "Erro ao excluir automação.");
    }
  };

  // Duplicar automação
  const handleDuplicate = async (e: React.MouseEvent, auto: Automation) => {
    e.stopPropagation();
    try {
      const payload = {
        nome: `${auto.nome} (Cópia)`,
        gatilho_item: auto.gatilho_item,
        gatilho_evento: auto.gatilho_evento,
        primeira_atividade_negocio: auto.primeira_atividade_negocio,
        passos: auto.automacao_passos || [],
      };
      const res = await createAutomation(payload, currentCrmId);
      setAutomations([res.data, ...automations]);
    } catch (err: any) {
      alert(err?.message || "Erro ao duplicar automação.");
    }
  };

  // Abrir construtor para nova automação
  const handleNewAutomation = () => {
    setSelectedAutomation(null);
    setActiveView("builder");
  };

  // Abrir construtor para editar
  const handleEdit = (auto: Automation) => {
    setSelectedAutomation(auto);
    setActiveView("builder");
  };

  // Voltar para listagem
  const handleBackToList = () => {
    setActiveView("list");
    loadAutomations();
  };

  // Filtragem local
  const filtered = automations.filter((a) => {
    if (itemFilter !== "todos" && a.gatilho_item !== itemFilter) return false;
    if (statusFilter === "ativas" && !a.ativa) return false;
    if (statusFilter === "pausadas" && a.ativa) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchName = a.nome?.toLowerCase().includes(term);
      const matchItem = a.gatilho_item?.toLowerCase().includes(term);
      if (!matchName && !matchItem) return false;
    }
    return true;
  });

  // Se estiver na view do construtor visual
  if (activeView === "builder") {
    return (
      <AutomationVisualBuilder
        initialAutomation={selectedAutomation}
        onBack={handleBackToList}
        onOpenHistory={(_autoId) => {
          setActiveView("list");
          setActiveTab("historico");
        }}
        crmId={currentCrmId}
      />
    );
  }

  return (
    <div className="flex-1 p-6 space-y-6 max-w-7xl mx-auto">
      {/* ── HEADER PRINCIPAL ─────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-primary/10 text-primary">
              <Zap className="w-5 h-5" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Automações do CRM
            </h1>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Crie fluxos inteligentes com gatilhos, condições condicionais e
            ações automáticas no CRM ou webhooks externos.
          </p>
        </div>

        <Button
          onClick={handleNewAutomation}
          className="gap-2 shadow-sm font-semibold text-xs"
        >
          <Plus className="w-4 h-4" />
          Nova Automação
        </Button>
      </div>

      {/* ── TABS: AUTOMAÇÕES VS HISTÓRICO ────────────────────────────────────── */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList className="bg-muted/50 p-1 border border-border/60">
          <TabsTrigger
            value="automacoes"
            className="text-xs gap-1.5 cursor-pointer"
          >
            <Zap className="w-3.5 h-3.5" />
            Automações ({automations.length})
          </TabsTrigger>
          <TabsTrigger
            value="historico"
            className="text-xs gap-1.5 cursor-pointer"
          >
            <History className="w-3.5 h-3.5" />
            Histórico de Execuções
          </TabsTrigger>
        </TabsList>

        {/* ── TAB 1: LISTA DE AUTOMAÇÕES ───────────────────────────────────────── */}
        <TabsContent value="automacoes" className="space-y-4">
          {/* BARRA DE PESQUISA E FILTROS */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-card/60 p-3.5 rounded-xl border border-border/80">
            <div className="flex items-center gap-2 flex-1 max-w-md">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 absolute left-3 top-3 text-muted-foreground" />
                <Input
                  placeholder="Buscar automação..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 h-9 text-xs bg-background"
                />
              </div>

              {/* Filtro por Registro */}
              <div className="flex items-center gap-1">
                {(
                  [
                    "todos",
                    "negocio",
                    "pessoa",
                    "organizacao",
                    "atividade",
                  ] as const
                ).map((it) => (
                  <button
                    key={it}
                    type="button"
                    onClick={() => setItemFilter(it)}
                    className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-colors cursor-pointer ${
                      itemFilter === it
                        ? "bg-primary text-primary-foreground shadow-xs"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    {it === "todos"
                      ? "Todos"
                      : TRIGGER_ITEM_LABELS[it as TriggerItem].label}
                  </button>
                ))}
              </div>

              {/* Filtro por Status */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-9 px-2.5 text-xs rounded-lg border border-border/80 bg-background text-foreground cursor-pointer"
              >
                <option value="todos">Todos os Status</option>
                <option value="ativas">Apenas Ativas</option>
                <option value="pausadas">Apenas Pausadas</option>
              </select>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={loadAutomations}
              disabled={loading}
              className="h-9 text-xs gap-1.5 self-end sm:self-auto"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
              />
              Atualizar
            </Button>
          </div>

          {/* GRID DE CARDS */}
          {loading ? (
            <div className="py-20 text-center text-xs text-muted-foreground flex flex-col items-center gap-2">
              <RefreshCw className="w-6 h-6 animate-spin text-primary" />
              Carregando fluxos de automação...
            </div>
          ) : error ? (
            <div className="p-6 text-center text-xs text-rose-500 bg-rose-500/5 rounded-xl border border-rose-500/20">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center border-2 border-dashed border-border/80 rounded-2xl bg-muted/10 space-y-3">
              <div className="p-4 rounded-full bg-primary/10 text-primary w-14 h-14 mx-auto flex items-center justify-center">
                <Sparkles className="w-7 h-7" />
              </div>
              <div className="max-w-md mx-auto space-y-1">
                <h3 className="font-bold text-base text-foreground">
                  Nenhuma automação encontrada
                </h3>
                <p className="text-xs text-muted-foreground">
                  Automatize tarefas repetitivas: crie tarefas automáticas
                  quando um lead mudar de etapa, envie webhooks para o n8n ou
                  notifique sua equipe.
                </p>
              </div>
              <Button onClick={handleNewAutomation} size="sm" className="gap-2">
                <Plus className="w-4 h-4" />
                Criar Minha Primeira Automação
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((auto) => {
                const totalPassos = auto.automacao_passos?.length || 0;
                const condicoesCount =
                  auto.automacao_passos?.filter((p) => p.tipo === "condicao")
                    .length || 0;
                const acoesCount =
                  auto.automacao_passos?.filter((p) => p.tipo === "acao")
                    .length || 0;

                const itemMeta = TRIGGER_ITEM_LABELS[auto.gatilho_item];
                const eventMeta = TRIGGER_EVENT_LABELS[auto.gatilho_evento];

                return (
                  <div
                    key={auto.id}
                    onClick={() => handleEdit(auto)}
                    className="group p-5 rounded-2xl border border-border/80 bg-card hover:bg-card/90 hover:border-primary/60 shadow-xs hover:shadow-md transition-all cursor-pointer flex flex-col justify-between"
                  >
                    <div>
                      {/* Topo do Card: Gatilho + Switch */}
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <Badge
                          variant="outline"
                          className="gap-1 text-[10px] uppercase tracking-wider font-semibold border-border/80 bg-muted/40"
                        >
                          {auto.gatilho_item === "negocio" && (
                            <Briefcase className="w-3 h-3 text-primary" />
                          )}
                          {auto.gatilho_item === "pessoa" && (
                            <User className="w-3 h-3 text-blue-500" />
                          )}
                          {auto.gatilho_item === "organizacao" && (
                            <Building className="w-3 h-3 text-amber-500" />
                          )}
                          {auto.gatilho_item === "atividade" && (
                            <CheckSquare className="w-3 h-3 text-emerald-500" />
                          )}
                          {itemMeta?.label}
                        </Badge>

                        <div
                          className="flex items-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Switch
                            checked={auto.ativa}
                            onCheckedChange={() =>
                              handleToggle(
                                { stopPropagation: () => {} } as any,
                                auto,
                              )
                            }
                          />

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="text-xs"
                            >
                              <DropdownMenuItem
                                onClick={() => handleEdit(auto)}
                                className="gap-2 cursor-pointer"
                              >
                                <Edit className="w-3.5 h-3.5" /> Editar no
                                Construtor
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => handleDuplicate(e as any, auto)}
                                className="gap-2 cursor-pointer"
                              >
                                <Copy className="w-3.5 h-3.5" /> Duplicar Fluxo
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={(e) => handleDelete(e as any, auto.id)}
                                className="gap-2 text-rose-600 cursor-pointer"
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Excluir
                                Automação
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      {/* Nome da Automação */}
                      <h3 className="font-bold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-1">
                        {auto.nome}
                      </h3>

                      {/* Descrição do Gatilho */}
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        Quando {itemMeta?.label.toLowerCase()} for{" "}
                        <strong className="text-foreground font-semibold">
                          {eventMeta?.label.toLowerCase()}
                        </strong>
                        {auto.gatilho_item === "atividade" &&
                        auto.primeira_atividade_negocio
                          ? " (1ª atividade)"
                          : ""}
                        .
                      </p>
                    </div>

                    {/* Rodapé do Card: Estatísticas de Passos */}
                    <div className="pt-4 mt-4 border-t border-border/60 flex items-center justify-between text-[11px] text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">
                          {totalPassos} {totalPassos === 1 ? "passo" : "passos"}
                        </span>
                        {condicoesCount > 0 && (
                          <span>• {condicoesCount} cond.</span>
                        )}
                        {acoesCount > 0 && <span>• {acoesCount} ações</span>}
                      </div>

                      <span className="text-[10px] text-muted-foreground/80">
                        {auto.ativa ? (
                          <span className="text-emerald-500 font-semibold">
                            ● Ativa
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Pausada</span>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── TAB 2: HISTÓRICO DE EXECUÇÕES ────────────────────────────────────── */}
        <TabsContent value="historico">
          <AutomationHistoryView crmId={currentCrmId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
